import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Breadcrumb,
  Button,
  ColorField,
  DurationPicker,
  EventColorPicker,
  ImageUpload,
  Input,
  PageHeader,
  ReminderPicker,
  SelectMenu,
  SettingsPanel,
  SettingsRow,
  Spinner,
  Switch,
  Textarea,
  type Reminder,
} from '@jordan/ui';
import type { OpenSlotConfig } from '@jordan/types';
import { AppChrome } from '../../components/AppChrome';
import { openSlotSidebar } from './openSlotSidebar';
import { MediaPicker } from '../../components/MediaPicker';
import { apiGet, apiPatch, apiPost, assetUrl } from '../../lib/api';
import { BOOKING_HOST_NAME } from '../../openslot/helpers';

interface FormOptions {
  schedules: { id: string; name: string }[];
  calendars: { id: string; summary: string }[];
  zoomAccounts: { id: string; label: string }[];
  defaults: OpenSlotConfig;
}

const DEFAULTS = {
  title: '', slug: '', description: '', eventTitleTemplate: '',
  durations: [30] as number[],
  bufferBefore: 0, bufferAfter: 0, minNoticeMin: 240, rangeDays: 60, slotIntervalMin: 30,
  locationType: 'GOOGLE_MEET', locationDetail: '',
  scheduleId: '', targetCalendarId: '', zoomAccountId: '', isActive: true,
  mediaImageId: '',
  // Google Calendar event colorId ('' = calendar default) and reminder notifications.
  eventColorId: '' as string,
  reminders: [{ method: 'popup', minutes: 60 }] as Reminder[],
  color: '#0ea5e9', colorAccent: '#0284c7', colorSelected: '#0284c7', colorAvailable: '#e0f2fe', colorTimeSlot: '#0ea5e9',
  colorAccentText: '#ffffff', colorSelectedText: '#ffffff', colorAvailableText: '#0f172a', colorTimeSlotText: '#0ea5e9',
  colorBase: '#f1f5f9', colorBaseText: '#1e293b',
};
type FormState = typeof DEFAULTS;
type PreviewKind = 'button' | 'selectedDay' | 'availableDay' | 'timeSlot' | 'base';

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** Put the default duration first, then the remaining stored durations in ascending order. */
const orderDurations = (stored: number[], def: number): number[] => {
  const rest = stored.filter((d) => d !== def).sort((a, b) => a - b);
  return [def, ...rest];
};

interface ColorGroup {
  label: string;
  color: keyof FormState;
  text: keyof FormState;
  preview: PreviewKind;
}
const COLOR_GROUPS: ColorGroup[] = [
  { label: 'Accent (buttons/links)', color: 'colorAccent', text: 'colorAccentText', preview: 'button' },
  { label: 'Selected day', color: 'colorSelected', text: 'colorSelectedText', preview: 'selectedDay' },
  { label: 'Available day', color: 'colorAvailable', text: 'colorAvailableText', preview: 'availableDay' },
  { label: 'Time slot', color: 'colorTimeSlot', text: 'colorTimeSlotText', preview: 'timeSlot' },
  { label: 'Base / nav', color: 'colorBase', text: 'colorBaseText', preview: 'base' },
];

const circle = (bg: string, fg: string, label: string) => (
  <div className="d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, borderRadius: '50%', background: bg, color: fg, fontWeight: 600 }}>
    {label}
  </div>
);

/** A small live preview of how a color pair looks on the public booking page. */
function ColorPreview({ kind, bg, fg }: { kind: PreviewKind; bg: string; fg: string }) {
  switch (kind) {
    case 'button':
      return <button type="button" className="btn rounded-3 fw-semibold" style={{ background: bg, color: fg, border: 'none' }}>Confirm</button>;
    case 'selectedDay':
      return circle(bg, fg, '8');
    case 'availableDay':
      return circle(bg, fg, '10');
    case 'timeSlot':
      return <button type="button" className="btn rounded-3 fw-semibold" style={{ border: `1px solid ${bg}`, color: fg, background: 'transparent' }}>8:00 AM</button>;
    case 'base':
      return <div className="rounded-3 px-3 py-2 fw-semibold d-inline-block" style={{ background: bg, color: fg }}>Base text</div>;
  }
}

export function EventTypeFormPage() {
  const { id } = useParams();
  const editing = id && id !== 'new';
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [opts, setOpts] = useState<FormOptions | null>(null);
  const [f, setF] = useState<FormState>(DEFAULTS);
  const [slugTouched, setSlugTouched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    apiGet<FormOptions>('/api/openslot/form-options')
      .then((o) => {
        setOpts(o);
        // Seed a brand-new event from the operator's Open Slot defaults (Configuration → Open Slot).
        if (!editing) {
          setF((p) => ({
            ...p,
            durations: [o.defaults.defaultDuration],
            bufferBefore: o.defaults.defaultBufferBefore,
            bufferAfter: o.defaults.defaultBufferAfter,
            minNoticeMin: o.defaults.defaultMinNotice,
            slotIntervalMin: o.defaults.defaultSlotInterval,
            rangeDays: o.defaults.defaultRangeDays,
            reminders: o.defaults.defaultReminders.length ? o.defaults.defaultReminders : p.reminders,
          }));
        }
      })
      .catch(() => setErr('Failed to load options.'));
    if (editing) {
      apiGet<Record<string, unknown>>(`/api/openslot/event-types/${id}`)
        .then((et) => {
          setF({
            ...DEFAULTS,
            ...et,
            description: (et.description as string) ?? '',
            eventTitleTemplate: (et.eventTitleTemplate as string) ?? '',
            locationDetail: (et.locationDetail as string) ?? '',
            scheduleId: (et.scheduleId as string) ?? '',
            targetCalendarId: (et.targetCalendarId as string) ?? '',
            zoomAccountId: (et.zoomAccountId as string) ?? '',
            mediaImageId: (et.mediaImageId as string) ?? '',
            eventColorId: (et.eventColorId as string) ?? '',
            reminders: (et.reminders as Reminder[]) ?? DEFAULTS.reminders,
            durations: orderDurations((et.durations as number[]) ?? [], (et.durationMin as number) ?? 30),
          } as FormState);
          setSlugTouched(true);
        })
        .catch(() => setErr('Failed to load event type.'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [editing, id]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    setErr(null);
    const durations = f.durations.length ? f.durations : [30];
    // The first duration is the default (durationMin); the rest are additional bookable lengths.
    const payload = { ...f, durations, durationMin: durations[0] };
    try {
      if (editing) await apiPatch(`/api/openslot/event-types/${id}`, payload);
      else await apiPost('/api/openslot/event-types', payload);
      navigate('/openslot/scheduling');
    } catch {
      setErr('Could not save — check the slug is unique and fields are valid.');
    } finally {
      setBusy(false);
    }
  };

  const num = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => set(k, Number(e.target.value) as never);
  const showZoom = useMemo(() => f.locationType === 'ZOOM', [f.locationType]);
  const showLocationDetail = f.locationType !== 'GOOGLE_MEET' && f.locationType !== 'ZOOM';

  const [pickerOpen, setPickerOpen] = useState(false);
  const uploadBrandImage = async (file: File, dataUrl: string) => {
    setErr(null);
    try {
      const img = await apiPost<{ id: string }>('/api/media', { dataUrl, label: file.name.replace(/\.[^.]+$/, '') });
      set('mediaImageId', img.id);
    } catch {
      setErr('Could not upload the image (admins only).');
    }
  };
  const brandImageUrl = f.mediaImageId ? assetUrl(`/api/media/${f.mediaImageId}/raw`) : null;

  return (
    <AppChrome secondarySidebar={openSlotSidebar(navigate, pathname)}>
      <PageHeader
        title={<>Open Slot - <span className="fw-normal">{editing ? 'Edit scheduling event' : 'New scheduling event'}</span></>}
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Scheduling', href: '/openslot/scheduling' }, { label: editing ? 'Edit' : 'New' }]} />}
      />
      <div className="content">
        {err && <Alert variant="danger" icon="ph-warning" dismissible>{err}</Alert>}
        {loading || !opts ? (
          <Spinner />
        ) : (
          <>
            <SettingsPanel title="Details" description="The basics shown to invitees on the booking page.">
              <SettingsRow title="Title" description="Shown as the heading of the booking page.">
                <Input
                  value={f.title}
                  onChange={(e) => {
                    set('title', e.target.value);
                    if (!slugTouched && !editing) set('slug', slugify(e.target.value));
                  }}
                />
              </SettingsRow>

              <SettingsRow title="Link" description="The public booking path. Use lowercase letters, numbers, and dashes.">
                <Input
                  value={f.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    set('slug', e.target.value);
                  }}
                  hint={`${BOOKING_HOST_NAME}/${f.slug || 'your-slug'}`}
                />
              </SettingsRow>

              <SettingsRow title="Description" description="Optional. Shown under the title on the booking page.">
                <Textarea value={f.description} onChange={(e) => set('description', e.target.value)} rows={3} />
              </SettingsRow>

              <SettingsRow title="Durations" description="Lengths the invitee can choose from. The default opens first on the booking page.">
                <DurationPicker value={f.durations} onChange={(v) => set('durations', v)} />
              </SettingsRow>

              <SettingsRow title="Active" description="Turn off to stop accepting new bookings without deleting the event.">
                <Switch label="Accepting bookings" checked={f.isActive} onChange={(e) => set('isActive', e.target.checked)} />
              </SettingsRow>
            </SettingsPanel>

            <SettingsPanel title="Location" description="Where the meeting takes place.">
              <SettingsRow title="Location" description="How you'll meet the invitee.">
                <SelectMenu
                  icon="ph-map-pin"
                  value={f.locationType}
                  onChange={(v) => set('locationType', v)}
                  options={[
                    { value: 'GOOGLE_MEET', label: 'Google Meet' },
                    { value: 'ZOOM', label: 'Zoom' },
                    { value: 'PHONE', label: 'Phone' },
                    { value: 'IN_PERSON', label: 'In person' },
                    { value: 'CUSTOM', label: 'Custom' },
                  ]}
                />
              </SettingsRow>

              {showLocationDetail && (
                <SettingsRow title="Location detail" description="Phone number, address, or link shared with the invitee.">
                  <Input value={f.locationDetail} onChange={(e) => set('locationDetail', e.target.value)} />
                </SettingsRow>
              )}

              {showZoom && (
                <SettingsRow title="Zoom account" description="Which connected Zoom account hosts the meeting.">
                  <SelectMenu
                    icon="ph-video-camera"
                    value={f.zoomAccountId}
                    onChange={(v) => set('zoomAccountId', v)}
                    options={[{ value: '', label: 'First connected account' }, ...opts.zoomAccounts.map((z) => ({ value: z.id, label: z.label }))]}
                  />
                </SettingsRow>
              )}
            </SettingsPanel>

            <SettingsPanel title="Availability & scheduling" description="When invitees can book and how slots are generated.">
              <SettingsRow title="Availability schedule" description="The weekly hours this event is bookable within.">
                <SelectMenu
                  icon="ph-calendar"
                  value={f.scheduleId}
                  onChange={(v) => set('scheduleId', v)}
                  options={[{ value: '', label: 'Default (Working hours)' }, ...opts.schedules.map((s) => ({ value: s.id, label: s.name }))]}
                />
              </SettingsRow>

              <SettingsRow title="Buffers" description="Padding kept free before and after each booking (minutes).">
                <div className="d-flex gap-3" style={{ maxWidth: 320 }}>
                  <Input label="Before" type="number" value={f.bufferBefore} onChange={num('bufferBefore')} />
                  <Input label="After" type="number" value={f.bufferAfter} onChange={num('bufferAfter')} />
                </div>
              </SettingsRow>

              <SettingsRow title="Minimum notice" description="Least lead time before a booking can start (minutes).">
                <div style={{ maxWidth: 200 }}>
                  <Input type="number" value={f.minNoticeMin} onChange={num('minNoticeMin')} />
                </div>
              </SettingsRow>

              <SettingsRow title="Time slot interval" description="Spacing between offered start times (minutes).">
                <div style={{ maxWidth: 200 }}>
                  <Input type="number" value={f.slotIntervalMin} onChange={num('slotIntervalMin')} />
                </div>
              </SettingsRow>

              <SettingsRow title="Booking window" description="How many days ahead invitees can book.">
                <div style={{ maxWidth: 200 }}>
                  <Input type="number" value={f.rangeDays} onChange={num('rangeDays')} />
                </div>
              </SettingsRow>
            </SettingsPanel>

            <SettingsPanel title="Calendar" description="How confirmed bookings appear on your calendar.">
              <SettingsRow title="Add bookings to" description="The calendar new bookings are written to.">
                <SelectMenu
                  icon="ph-calendar-check"
                  value={f.targetCalendarId}
                  onChange={(v) => set('targetCalendarId', v)}
                  options={[{ value: '', label: 'First writable calendar' }, ...opts.calendars.map((c) => ({ value: c.id, label: c.summary }))]}
                />
              </SettingsRow>

              <SettingsRow title="Event title template" description="Title of the created calendar event. Variables: {{name}}, {{email}}, {{title}}.">
                <Input value={f.eventTitleTemplate} onChange={(e) => set('eventTitleTemplate', e.target.value)} />
              </SettingsRow>

              <SettingsRow title="Meeting color" description="The color of the event on your Google Calendar.">
                <EventColorPicker value={f.eventColorId || null} onChange={(v) => set('eventColorId', v ?? '')} />
              </SettingsRow>

              <SettingsRow title="Reminders" description="Notifications added to the calendar event. Mirrors Google Calendar reminders.">
                <ReminderPicker value={f.reminders} onChange={(v) => set('reminders', v)} />
              </SettingsRow>
            </SettingsPanel>

            <SettingsPanel title="Booking page branding" description="The logo and colors used on the public booking page.">
              <SettingsRow title="Booking logo" description="Shown in the top-left of the booking page. Pick from the Media Library or upload a new image.">
                <div className="d-flex align-items-center gap-3 mb-3">
                  {brandImageUrl ? (
                    <img src={brandImageUrl} alt="" style={{ height: 48, maxWidth: 160, objectFit: 'contain' }} className="border rounded p-1" />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center border rounded text-muted" style={{ width: 48, height: 48 }}>
                      <i className="ph-image" style={{ fontSize: 22 }} />
                    </div>
                  )}
                  <div className="d-flex flex-wrap gap-2">
                    <Button variant="light" icon="ph-images" onClick={() => setPickerOpen(true)}>Choose from library</Button>
                    {f.mediaImageId && (
                      <Button variant="light" className="text-danger" icon="ph-x" onClick={() => set('mediaImageId', '')}>Remove</Button>
                    )}
                  </div>
                </div>
                <ImageUpload
                  previewSrc={null}
                  onSelect={(file, dataUrl) => void uploadBrandImage(file, dataUrl)}
                  hint="Uploading also adds the image to the Media Library."
                />
              </SettingsRow>

              <SettingsRow title="Logo dash color" description="Accent dash shown when no logo is set.">
                <div style={{ maxWidth: 220 }}>
                  <ColorField value={f.color} onChange={(v) => set('color', v)} />
                </div>
              </SettingsRow>

              <SettingsRow title="Color scheme" description="Each color pair with a live preview of how it looks on the booking page.">
                <div className="row fw-semibold fs-sm text-muted mb-2 d-none d-lg-flex">
                  <div className="col-lg-4">Color</div>
                  <div className="col-lg-4">Text color</div>
                  <div className="col-lg-4">Preview</div>
                </div>
                {COLOR_GROUPS.map((g) => (
                  <div className="row align-items-center pb-2 mb-2 border-bottom" key={g.color}>
                    <div className="col-lg-4">
                      <ColorField label={g.label} value={f[g.color] as string} onChange={(v) => set(g.color, v as never)} />
                    </div>
                    <div className="col-lg-4">
                      <ColorField label={`${g.label} text`} value={f[g.text] as string} onChange={(v) => set(g.text, v as never)} />
                    </div>
                    <div className="col-lg-4">
                      <ColorPreview kind={g.preview} bg={f[g.color] as string} fg={f[g.text] as string} />
                    </div>
                  </div>
                ))}
              </SettingsRow>
            </SettingsPanel>

            <div className="d-flex justify-content-end gap-2 mb-3">
              <Button variant="light" onClick={() => navigate('/openslot/scheduling')}>Cancel</Button>
              <Button variant="primary" disabled={busy || !f.title || !f.slug} onClick={save}>
                {editing ? 'Save changes' : 'Create scheduling event'}
              </Button>
            </div>
          </>
        )}
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(img) => { set('mediaImageId', img.id); setPickerOpen(false); }}
      />
    </AppChrome>
  );
}
